import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../../api/client";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Alert, AlertDescription } from "../../components/ui/alert";
import { Loader2, ShieldCheck, Mail, Lock, Eye, EyeOff, ArrowLeft } from "lucide-react";

export default function CreateAdminPage() {
 const navigate = useNavigate();
 const [email, setEmail] = useState("");
 const [password, setPassword] = useState("");
 const [confirm, setConfirm] = useState("");
 const [showPwd, setShowPwd] = useState(false);
 const [loading, setLoading] = useState(false);
 const [error, setError] = useState(null);
 const [success, setSuccess] = useState(null);

 const passwordScore = getPasswordScore(password);
 const passwordTooShort = password.length < 8;

 function resetForm() {
 setEmail("");
 setPassword("");
 setConfirm("");
 setError(null);
 setSuccess(null);
 }

 async function handleSubmit(e) {
 e.preventDefault();
 setError(null);
 setSuccess(null);

 if (!email.trim()) return setError("Email is required.");
 if (!password) return setError("Password is required.");
 if (password !== confirm) return setError("Passwords do not match.");
 if (passwordTooShort) return setError("Password must be at least 8 characters.");
 if (passwordScore < 2) return setError("Choose a stronger password.");

 setLoading(true);

 try {
 await api.post("admin/create-admin", { email, password });
 setSuccess("Admin created successfully!");
 resetForm();
 } catch (err) {
 const msg = err.response?.data?.message;
 setError(msg || "Failed to create admin");
 } finally {
 setLoading(false);
 }
 }

 return (
 <div className="min-h-screen py-6 md:py-10">
 <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
 {/* Header */}
 <div className="mb-8">
 <Button 
 variant="outline" 
 onClick={() => navigate("/admin/dashboard")}
 className="mb-6"
 >
 <ArrowLeft className="w-4 h-4 mr-2" />
 Back to Dashboard
 </Button>
 
 <div className="flex items-center gap-3 mb-2">
 <div className="p-2.5 rounded-xl bg-gradient-to-r from-primary to-accent shadow-lg">
 <ShieldCheck className="w-6 h-6 text-white" />
 </div>
 <h1 className="text-3xl md:text-4xl font-bold">Create Admin</h1>
 </div>
 <p className="text-muted-foreground">Create a new administrator account</p>
 </div>

 {error && (
 <Alert variant="destructive" className="mb-6">
 <AlertDescription>{error}</AlertDescription>
 </Alert>
 )}

 {success && (
 <Alert className="mb-6 border-green-500 bg-green-500/100/10 text-green-600">
 <AlertDescription>{success}</AlertDescription>
 </Alert>
 )}

 <Card>
 <CardHeader>
 <CardTitle>Admin Details</CardTitle>
 <CardDescription>
 Only existing admins should access this page
 </CardDescription>
 </CardHeader>
 <CardContent>
 <form onSubmit={handleSubmit} className="space-y-6">
 {/* Email */}
 <div className="space-y-2">
 <Label htmlFor="email">Email Address</Label>
 <div className="relative">
 <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
 <Input
 id="email"
 type="email"
 required
 value={email}
 onChange={(e) => setEmail(e.target.value)}
 placeholder="admin@example.com"
 className="pl-10"
 />
 </div>
 </div>

 {/* Password */}
 <div className="space-y-2">
 <Label htmlFor="password">Password</Label>
 <div className="relative">
 <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
 <Input
 id="password"
 type={showPwd ? "text" : "password"}
 value={password}
 onChange={(e) => setPassword(e.target.value)}
 placeholder="Choose a strong password"
 className="pl-10 pr-10"
 />
 <button
 type="button"
 onClick={() => setShowPwd(!showPwd)}
 className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
 >
 {showPwd ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
 </button>
 </div>

 <div className="mt-2 flex items-center justify-between text-sm">
 <div className="flex items-center gap-3">
 <PasswordStrengthBar score={passwordScore} />
 <span className="text-xs text-muted-foreground">
 {passwordTooShort ? "Too short" : strengthLabel(passwordScore)}
 </span>
 </div>
 <span className="text-xs text-muted-foreground">Min 8 characters</span>
 </div>
 </div>

 {/* Confirm Password */}
 <div className="space-y-2">
 <Label htmlFor="confirm">Confirm Password</Label>
 <div className="relative">
 <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
 <Input
 id="confirm"
 type={showPwd ? "text" : "password"}
 value={confirm}
 onChange={(e) => setConfirm(e.target.value)}
 placeholder="Repeat password"
 className="pl-10"
 />
 </div>
 </div>

 {/* Buttons */}
 <div className="flex flex-col sm:flex-row gap-3 pt-4">
 <Button
 type="submit"
 disabled={loading}
 className="flex-1"
 >
 {loading ? (
 <>
 <Loader2 className="w-4 h-4 mr-2 animate-spin" />
 Creating...
 </>
 ) : (
 <>
 <ShieldCheck className="w-4 h-4 mr-2" />
 Create Admin
 </>
 )}
 </Button>
 <Button
 type="button"
 variant="outline"
 onClick={resetForm}
 disabled={loading}
 className="flex-1"
 >
 Reset
 </Button>
 </div>
 </form>
 </CardContent>
 </Card>
 </div>
 </div>
 );
}

/* --- Helpers --- */

function PasswordStrengthBar({ score = 0 }) {
 const colors = [
 "bg-red-500",
 "bg-orange-500", 
 "bg-yellow-500/100",
 "bg-lime-500",
 "bg-green-500/100"
 ];
 
 return (
 <div className="w-32 flex gap-1">
 {[0, 1, 2, 3, 4].map((i) => (
 <div
 key={i}
 className={`h-2 flex-1 rounded-sm transition-colors ${
 i <= score ? colors[score] : ""
 }`}
 />
 ))}
 </div>
 );
}

function getPasswordScore(pwd) {
 if (!pwd) return 0;
 let score = 0;
 if (pwd.length >= 8) score++;
 if (/[A-Z]/.test(pwd) && /[a-z]/.test(pwd)) score++;
 if (/\d/.test(pwd)) score++;
 if (/[^A-Za-z0-9]/.test(pwd)) score++;
 return Math.min(score, 4);
}

function strengthLabel(score) {
 return ["Weak", "Weak", "Okay", "Good", "Strong"][score] || "";
}
